import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { MapPin, Loader2 } from "lucide-react";

export const AddressAutocomplete = ({ value, onChange }) => {
  const [q, setQ] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  const onType = (val) => {
    setQ(val);
    onChange(val);
    setOpen(true);
    clearTimeout(timer.current);
    if (val.trim().length < 3) { setSuggestions([]); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const s = await api.suggestAddress(val);
        setSuggestions(s);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const choose = (s) => {
    onChange(s.display_name);
    setQ(s.display_name);
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <Input
          data-testid="address-input"
          value={q}
          onChange={(e) => onType(e.target.value)}
          onFocus={() => q.trim().length >= 3 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Start typing a NZ address..."
          autoComplete="off"
          className="pl-9"
        />
      </div>
      {open && q.trim().length >= 3 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-auto" data-testid="address-suggestions">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching NZ addresses...
            </div>
          )}
          {!loading && suggestions.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400">No matching addresses found.</div>
          )}
          {!loading && suggestions.map((s, i) => (
            <button
              key={s.display_name || i}
              type="button"
              data-testid={`address-suggestion-${i}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(s)}
              className="flex items-start gap-2 w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 border-b border-slate-50 last:border-0"
            >
              <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
              <span>{s.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
